'use client'

import { QueryClientProvider, type QueryClient } from '@tanstack/react-query'
import { createTRPCClient, httpBatchLink, loggerLink } from '@trpc/client'
import { createTRPCContext } from '@trpc/tanstack-react-query'
import { type inferRouterInputs, type inferRouterOutputs } from '@trpc/server'
import { useState } from 'react'
import SuperJSON from 'superjson'

import { type AppRouter } from '@/backend/api/root'
import { createQueryClient } from './query-client'
import { env } from '@/env'
import { readSSROnlySecret } from 'ssr-only-secrets'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

let clientQueryClientSingleton: QueryClient | undefined = undefined
const getQueryClient = () => {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return createQueryClient()
  }
  // Browser: use singleton pattern to keep the same query client
  clientQueryClientSingleton ??= createQueryClient()
  return clientQueryClientSingleton
}

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>()

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>

export function TRPCReactProvider(props: {
  children: React.ReactNode
  ssrOnlySecret: string
}) {
  const queryClient = getQueryClient()

  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        loggerLink({
          enabled: (op) =>
            env.NEXT_PUBLIC_APP_ENV === 'development' ||
            (op.direction === 'down' && op.result instanceof Error),
        }),
        httpBatchLink({
          transformer: SuperJSON,
          url: getBaseUrl() + '/api/trpc',
          headers: async () => {
            const headers = new Headers()
            headers.set('x-trpc-source', 'nextjs-react')

            const cookieHeaderValue = await readSSROnlySecret(
              props.ssrOnlySecret,
              'SECRET_CLIENT_COOKIE_VAR',
            )
            if (cookieHeaderValue) {
              headers.set('cookie', cookieHeaderValue)
            }

            return headers
          },
        }),
      ],
    }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {props.children}
        {env.NEXT_PUBLIC_APP_ENV !== 'production' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </TRPCProvider>
    </QueryClientProvider>
  )
}

function getBaseUrl() {
  if (typeof window !== 'undefined') return window.location.origin
  return `http://localhost:${process.env.PORT ?? 3000}`
}
